import React from 'react';

import type { MessageDescriptor } from 'react-intl-next';

import { CONFIG_ITEM_KEYS } from '../../config-items/config-item-keys';
import { createEditorPluginAIConfigItemMarkdown } from '../../config-items/config-items';
import { sliceOrNodeToMarkdown } from '../../config-items/slice-or-node-to-markdown';
import { streamConvoAI } from '../../provider/prompt-requests/convo-ai';
import { Icon } from '../assets/icons/prefill';
import { createInsertAtCurrentPosition } from '../config-item-actions/markdown';

import { messages } from './messages';
import { PrefillPrompts } from './types';

const SELECTION_TYPE = 'empty';

export const prefillPromptRecord: Record<
	PrefillPrompts,
	{ key: CONFIG_ITEM_KEYS; message: MessageDescriptor }
> = {
	[PrefillPrompts.CompetitiveAnalysis]: {
		key: CONFIG_ITEM_KEYS.PREFILL_COMPETITIVE_ANALYSIS,
		message: messages.competitiveAnalysisPrompt,
	},
	[PrefillPrompts.DevOps]: {
		key: CONFIG_ITEM_KEYS.PREFILL_DEVOPS,
		message: messages.devOpsPrompt,
	},
	[PrefillPrompts.ExperimentPlan]: {
		key: CONFIG_ITEM_KEYS.PREFILL_EXPERIMENT_PLAN,
		message: messages.experimentPlanPrompt,
	},
	[PrefillPrompts.MarketingCampaign]: {
		key: CONFIG_ITEM_KEYS.PREFILL_MARKETING_CAMPAIGN,
		message: messages.marketingCampaignPrompt,
	},
	[PrefillPrompts.ProductLaunchAnnouncement]: {
		key: CONFIG_ITEM_KEYS.PREFILL_PRODUCT_LAUNCH_ANNOUNCEMENT,
		message: messages.productLaunchAnnouncementPrompt,
	},
	[PrefillPrompts.ProductRoadmap]: {
		key: CONFIG_ITEM_KEYS.PREFILL_PRODUCT_ROADMAP,
		message: messages.productRoadmapPrompt,
	},
	[PrefillPrompts.SprintPlanning]: {
		key: CONFIG_ITEM_KEYS.PREFILL_SPRINT_PLANNING,
		message: messages.sprintPlanningPrompt,
	},
};

export const createPrefillConfigItem = (prompt: PrefillPrompts) => {
	const { key, message } = prefillPromptRecord[prompt];
	return createEditorPluginAIConfigItemMarkdown({
		key,
		title: message,
		description: message,
		selectionType: SELECTION_TYPE,
		icon: ({ shownAt }) => <Icon shownAt={shownAt} />,
		getInitialContext: ({ editorView, intl, updateIdMap, mentionMap }) => {
			/**
			 * This isn't actually used since it just uses free generate
			 */
			const { markdown, contentStatistics } = sliceOrNodeToMarkdown({
				node: editorView.state.doc,
				editorView,
				convertTo: 'markdown-plus',
				updateIdMap,
				selectionType: SELECTION_TYPE,
				mentionMap,
			});
			return {
				document: markdown,
				userLocale: intl.locale,
				intentSchemaId: 'custom_free_generate_intent_schema.json',
				contentStatistics,
			};
		},
		triggerPromptRequest({ initialContext, promptInput, editorSchema, analyticsContext }) {
			/**
			 * This isn't actually used since it just uses free generate
			 */
			return streamConvoAI({
				aiSessionId: analyticsContext?.aiSessionId,
				customPrompt: promptInput!,
				userLocale: initialContext.userLocale,
				intentSchemaId: 'custom_free_generate_intent_schema.json',
				editorSchema,
				fullDocument: initialContext.document,
			});
		},
		isVisible: ({ editorView }) => !(editorView.state.doc.nodeSize - 4),
		getBackendModel: () => 'assistance-service',
		disableInterrogation: () => false,
		actions: [createInsertAtCurrentPosition({ appearance: 'primary' })],
		isPrefill: true,
	});
};
export const prefillConfigItems = [
	createPrefillConfigItem(PrefillPrompts.MarketingCampaign),
	createPrefillConfigItem(PrefillPrompts.CompetitiveAnalysis),
	createPrefillConfigItem(PrefillPrompts.SprintPlanning),
	createPrefillConfigItem(PrefillPrompts.ExperimentPlan),
	createPrefillConfigItem(PrefillPrompts.DevOps),
	createPrefillConfigItem(PrefillPrompts.ProductRoadmap),
	createPrefillConfigItem(PrefillPrompts.ProductLaunchAnnouncement),
];
